import type { KeyboardEvent } from 'react';
import styled from 'styled-components';
import type { AvatarSizes } from '@frameio/vapor';
import { Separator, theme, whenFocused } from '@frameio/vapor';
import { IconChevronLeft } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/ProjectHeaderBackButton';
import { ProjectHeaderContent } from './ProjectHeaderContent';

export type ProjectHeaderBackButtonProps = {
  avatarSize?: AvatarSizes;
  avatarUrl: string;
  details: string;
  isPrivate?: boolean;
  onSelect: () => void;
  projectId: string;
  projectName: string;
};

export function ProjectHeaderBackButton({
  avatarSize,
  avatarUrl,
  details,
  isPrivate,
  onSelect,
  projectId,
  projectName,
}: ProjectHeaderBackButtonProps) {
  return (
    <>
      <Container
        aria-label={TEXT.backButtonLabel(projectName)}
        data-testid="project-tree-header"
        onClick={onSelect}
        onKeyDown={(event: KeyboardEvent) => {
          if (event.key === 'Enter') {
            onSelect();
          } else if (event.key === ' ') {
            event.preventDefault();
            onSelect();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <LeftChevronContainer>
          <IconChevronLeft color={theme.colors.icon.primary} size={24} />
        </LeftChevronContainer>

        <ProjectHeaderContent
          avatarSize={avatarSize}
          avatarUrl={avatarUrl}
          details={details}
          isPrivate={isPrivate}
          projectId={projectId}
          projectName={projectName}
          truncateName={true}
        />
      </Container>
      <SeparatorContainer>
        <Separator color="primary" />
      </SeparatorContainer>
    </>
  );
}

const HEADER_HEIGHT = 64;

const Container = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  height: ${theme.spaces[HEADER_HEIGHT]};
  min-width: 0;
  outline: none;
  padding-left: ${theme.spaces[16]};
  padding-right: ${theme.spaces[12]};
  width: 100%;

  ${whenFocused} {
    border-radius: ${theme.radii[8]};
    box-shadow: ${theme.shadows.interactive.focus};
  }
`;

const LeftChevronContainer = styled.div`
  margin-right: ${theme.spaces[12]};
`;

const SeparatorContainer = styled.div`
  margin-top: -1px;
`;

TEXT.DEFINE({
  backButtonLabel: (projectName: string): string => `Back to ${projectName}`,
});
